import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Smartphone, Landmark, CheckCircle2, Loader2, AlertTriangle, Edit3 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NETWORK_LABELS = { mtn: "MTN Mobile Money", airtel: "Airtel Money", zamtel: "Zamtel Kwacha" };

/**
 * HostPayoutMethodCard — banner-style on the host dashboard.
 *
 * • Not set: shows an amber "Payout destination not set" prompt with a copper
 *   "Set up now" button — this is the onboarding step for new hosts.
 * • Set:    shows the current method (masked account number) with an Edit button.
 * • Edit dialog: toggle mobile-money / bank, fill the network-specific fields,
 *   POST to /api/host/payout-method — same endpoint handles both create + update.
 */
export default function HostPayoutMethodCard() {
  const [pm, setPm] = useState(undefined); // undefined = loading, null = not set
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    method_type: "mobile_money",
    mm_network: "mtn",
    mm_number: "",
    bank_name: "",
    account_name: "",
    account_number: "",
  });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/host/payout-method`);
      setPm(data.payout_method || null);
    } catch (e) {
      setPm(null);
    }
  };
  useEffect(() => { load(); }, []);

  const openDialog = () => {
    // Seed the form with existing values so Edit doesn't wipe them.
    if (pm?.method_type === "mobile_money") {
      setForm((f) => ({ ...f, method_type: "mobile_money", mm_network: pm.mm_network || "mtn", mm_number: pm.mm_number || "" }));
    } else if (pm?.method_type === "bank") {
      setForm((f) => ({
        ...f, method_type: "bank",
        bank_name: pm.bank_name || "", account_name: pm.account_name || "", account_number: pm.account_number || "",
      }));
    }
    setOpen(true);
  };

  const submit = async () => {
    const body = { method_type: form.method_type };
    if (form.method_type === "mobile_money") {
      Object.assign(body, { mm_network: form.mm_network, mm_number: form.mm_number.trim() });
    } else {
      Object.assign(body, {
        bank_name: form.bank_name.trim(),
        account_name: form.account_name.trim(),
        account_number: form.account_number.trim(),
      });
    }
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/host/payout-method`, body);
      setPm(data.payout_method);
      toast.success("Payout destination saved — the admin can now pay you directly");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your payout details");
    } finally {
      setBusy(false);
    }
  };

  const maskAcct = (n) => {
    if (!n) return "";
    return n.length <= 4 ? n : `••••${n.slice(-4)}`;
  };

  if (pm === undefined) return null;

  return (
    <>
      {pm === null ? (
        <div
          data-testid="payout-method-card-unset"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
            <div>
              <div className="font-display text-sm font-bold text-amber-900">Set up your payout destination</div>
              <div className="mt-0.5 text-xs text-amber-800">
                Choose Mobile Money or a bank account so EasyStay can pay you after each stay. You can update it any time.
              </div>
            </div>
          </div>
          <button
            data-testid="payout-method-setup-btn"
            onClick={openDialog}
            className="btn-primary text-xs"
          >
            <Wallet className="mr-1 inline h-3.5 w-3.5" /> Set up now
          </button>
        </div>
      ) : (
        <div
          data-testid="payout-method-card"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 card-shadow"
        >
          <div className="flex items-start gap-3">
            {pm.method_type === "mobile_money" ? (
              <Smartphone className="mt-0.5 h-5 w-5 flex-shrink-0 text-copper" />
            ) : (
              <Landmark className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-sm font-bold text-slate-900">Payouts sent to</div>
                <CheckCircle2 className="h-4 w-4 text-forest" />
              </div>
              {pm.method_type === "mobile_money" ? (
                <div data-testid="payout-method-summary" className="mt-0.5 text-xs text-slate-600">
                  <span className="font-semibold">{NETWORK_LABELS[pm.mm_network] || pm.mm_network}</span>
                  {" · "}
                  <span className="font-mono">{pm.mm_number}</span>
                </div>
              ) : (
                <div data-testid="payout-method-summary" className="mt-0.5 text-xs text-slate-600">
                  <span className="font-semibold">{pm.bank_name}</span>
                  {" · "}{pm.account_name}
                  {" · "}<span className="font-mono">{maskAcct(pm.account_number)}</span>
                </div>
              )}
            </div>
          </div>
          <button
            data-testid="payout-method-edit-btn"
            onClick={openDialog}
            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-copper hover:text-copper"
          >
            <Edit3 className="h-3 w-3" /> Edit
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="payout-method-dialog">
          <DialogHeader>
            <DialogTitle>Where should EasyStay pay you?</DialogTitle>
            <DialogDescription>
              Choose Mobile Money or bank transfer. You can change this at any time from your host dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2" data-testid="payout-method-type-toggle">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, method_type: "mobile_money" }))}
                data-testid="payout-type-mobile"
                className={`rounded-2xl border p-3 text-left transition ${
                  form.method_type === "mobile_money"
                    ? "border-copper bg-copper/5"
                    : "border-slate-200 hover:border-copper/60"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <Smartphone className="h-4 w-4 text-copper" /> Mobile Money
                </div>
                <div className="mt-1 text-[11px] text-slate-500">MTN, Airtel or Zamtel</div>
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, method_type: "bank" }))}
                data-testid="payout-type-bank"
                className={`rounded-2xl border p-3 text-left transition ${
                  form.method_type === "bank"
                    ? "border-forest bg-forest/5"
                    : "border-slate-200 hover:border-forest/60"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <Landmark className="h-4 w-4 text-forest" /> Bank Account
                </div>
                <div className="mt-1 text-[11px] text-slate-500">Zanaco, FNB, Stanbic, etc.</div>
              </button>
            </div>

            {form.method_type === "mobile_money" ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Preferred network</label>
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="mm-network-picker">
                    {[
                      { key: "mtn", label: "MTN", tone: "border-yellow-300 bg-yellow-50" },
                      { key: "airtel", label: "Airtel", tone: "border-rose-300 bg-rose-50" },
                      { key: "zamtel", label: "Zamtel", tone: "border-emerald-300 bg-emerald-50" },
                    ].map((n) => (
                      <button
                        key={n.key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, mm_network: n.key }))}
                        data-testid={`mm-network-${n.key}`}
                        className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                          form.mm_network === n.key ? `${n.tone} ring-2 ring-copper` : "border-slate-200 hover:border-copper/50"
                        }`}
                      >
                        {n.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Mobile money number</label>
                  <input
                    data-testid="mm-number-input"
                    type="tel"
                    value={form.mm_number}
                    onChange={(e) => setForm((f) => ({ ...f, mm_number: e.target.value }))}
                    placeholder="+260 97 X XX XX XX"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm outline-none focus:border-copper focus:bg-white"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bank name</label>
                  <input
                    data-testid="bank-name-input"
                    type="text"
                    value={form.bank_name}
                    onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                    placeholder="Zanaco, Stanbic, FNB…"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Account name</label>
                  <input
                    data-testid="bank-account-name-input"
                    type="text"
                    value={form.account_name}
                    onChange={(e) => setForm((f) => ({ ...f, account_name: e.target.value }))}
                    placeholder="Name on the account"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Account number</label>
                  <input
                    data-testid="bank-account-number-input"
                    type="text"
                    value={form.account_number}
                    onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
                    placeholder="12 3456 7890"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm outline-none focus:border-forest focus:bg-white"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="btn-outline text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="payout-method-save-btn"
              className="btn-primary text-sm disabled:opacity-50"
            >
              {busy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />}
              Save payout details
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
